import React, { createContext, useContext, useEffect, useState } from "react";
import { CsrAPI } from "../apis/CsrAPI";
import { Outlet } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useSession } from "../contexts/SessionContext";

const FeatureFlagContext = createContext(null);

export const FeatureFlagProvider = () => {
  const [ajaxLoading, setAjaxLoading] = useState(true);
  const [featureFlags, setFeatureFlags] = useState({});
  const [errMsg, setErrMsg] = useState("");
  const { isAuthenticated, isLoading } = useAuth0();
  const { sessionReady } = useSession();
  const isProduction = window.location.hostname === "app.trilodocs.com";

  useEffect(() => {
    if (isLoading || !sessionReady) {
      return;
    }

    if (isAuthenticated && Object.keys(featureFlags).length === 0) {
      CsrAPI.getFeatureFlags()
        .then(response => {
          const data = response.data;
          data.PATHS.push("data-mapping");
          data.PATHS.push("advancedsearch");
          data.PATHS.push("lay-summary");
          data.PATHS.push("lay-summary-questions");
          data.PATHS.push("lay-summary-file");
          data.PATHS.push("select-study");
          if (!isProduction) {
            data.PATHS.push("apitest");
          }
          data.PATHS.push("stdpharmacokinetics");
          data.PATHS.push("comppharmacokinetics");
          data.PATHS.push("bioavailability");
          data.PATHS.push("stdpharmacodynamics");
          data.PATHS.push("comppharmacodynamics");
          data.PATHS.push("study-design-demographics");
          data.PATHS.push("efficacy-analysis");
          data.PATHS.push("safety-analysis");
          data.PATHS.push("table-analysis-questions");
          data.PATHS.push("table-analysis-ai-response");
          setFeatureFlags(data);
        })
        .catch(() => {
          setErrMsg("Failed to get feature flags");
        })
        .finally(() => {
          setAjaxLoading(false);
        });
    } else if (!isAuthenticated && !isLoading) {
      setFeatureFlags({});
      setAjaxLoading(false);
    }
  }, [isAuthenticated, isLoading, sessionReady]);

  const isPathAllowed = path => {
    if (!path) return true;
    const targetPath = path.split("/").pop();
    if (
      (path.includes("table-analysis-ai-response") &&
        featureFlags?.PATHS?.includes("table-analysis-ai-response")) ||
      (path.includes("table-analysis-questions") &&
        featureFlags?.PATHS?.includes("table-analysis-questions"))
    ) {
      return true;
    }
    return featureFlags?.PATHS?.includes(targetPath) || false;
  };

  return (
    <FeatureFlagContext.Provider
      value={{
        ajaxLoading,
        featureFlags,
        errMsg,
        isPathAllowed,
      }}>
      <Outlet />
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);

  if (!context) {
    throw new Error("Please use FeatureFlagProvider in parent component");
  }

  return context;
};
